import { useParams } from 'react-router-dom';

import { GridRowModel } from '@mui/x-data-grid';
import { useAppSelector } from 'app/hooks';
import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { selectPageInfo } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { Roles } from 'common/constants/roles';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { GetDeleteDialogContent } from 'features/aclEntries/AclEntriesDeleteDialogContent';
import {
    AclEntiriesDeleteDialogProperties,
    AclEntriesDrawerProperties,
    AclEntriesPage
} from 'features/aclEntries/aclEntriesTypeDefs';

import { GetDrawerContent } from '../aclEntries/AclEntriesDrawerContent';

import { _createUserAclEntriesActionDefinitions, _createUserAclEntriesDataGridColumns } from './helpers';
import { useFetchUserAclEntriesDefaultDataset } from './hooks/useFetchUserAclEntries';
import { useFetchUserAclEntriesDetailsWithLicenseInfo } from './hooks/useFetchUserAclEntriesDetails';
import { addNewFilter, clearFilters, removeFilter, selectFilters } from './userAclEntriesSlice';

export function UserAclEntriesTab() {

    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);

    const [sortModel, setSortModel] = usePalmSortModel('resourceTableName');
    const { tenantId = '', userId = '' } = useParams();
    const pageInfo = useAppSelector(selectPageInfo);

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters);

    const {
        aclEntriesDefaultDatasetProcessing,
        refreshUserAclEntriesDefaultDataset
    } = useFetchUserAclEntriesDefaultDataset({
        tenantId,
        userId,
        isLicenseRequired: pageInfo?.tenantRequiresLicense
    });

    const {
        aclEntriesWithLicenseInfoProcessing,
        refreshUserAclEntriesDetailsWithLicenseInfo
    } = useFetchUserAclEntriesDetailsWithLicenseInfo({
        tenantId,
        userId,
        isLicenseRequired: pageInfo?.tenantRequiresLicense
    });

    const refreshUserAclEntriesDataGridCallback = pageInfo?.tenantRequiresLicense
        ? refreshUserAclEntriesDetailsWithLicenseInfo
        : refreshUserAclEntriesDefaultDataset;

    const disabledEditCallback = (row: GridRowModel) => {
        return  shouldDisableButtons || pageInfo?.tenantRequiresLicense != true && row.conditions?.length == 0;
    };

    const columnDefinitions = _createUserAclEntriesDataGridColumns(pageInfo?.tenantRequiresLicense);
    const actionDefinitions = _createUserAclEntriesActionDefinitions(
        shouldDisableButtons,
        disabledEditCallback
    );

    function GetTenantUserAclDrawer() {
        const drawerProps : AclEntriesDrawerProperties = {
            principalId: userId,
            tenantId: tenantId,
            tenantRequiresLicense: pageInfo?.tenantRequiresLicense ?? false,
            tenantName: pageInfo?.name ?? '',
            principalName: `${pageInfo?.firstName ?? ''} ${pageInfo?.lastName ?? ''}`,
            aclPage: AclEntriesPage.TenantUser
        };
        return GetDrawerContent(drawerProps);
    }

    function GetAclDeleteDialogContent() {
        const dialogProps: AclEntiriesDeleteDialogProperties = {
            aclPage: AclEntriesPage.TenantUser,
            principalName: `${pageInfo?.firstName ?? ''} ${pageInfo?.lastName ?? ''}`,
            principalId: userId,
            tenantRequiresLicense: pageInfo?.tenantRequiresLicense
        };
        return GetDeleteDialogContent(dialogProps);
    }

    return <PalmDataGrid
        title='ACL Entry'
        processing={
            pageInfo?.tenantRequiresLicense ?
                aclEntriesWithLicenseInfoProcessing
                : aclEntriesDefaultDatasetProcessing
        }
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        contentFunction={GetTenantUserAclDrawer()}
        filter={filterProp}
        deleteFunction={GetAclDeleteDialogContent()}
        refreshDataGridClick={refreshUserAclEntriesDataGridCallback}
        isCreateButtonDisabled={shouldDisableButtons}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;
}
